import React from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, Sparkles } from "lucide-react";
import { calendlyUrl, calendlyLinkProps } from "../../lib/calendly";
import Chapter from "../site/Chapter";

const packages = [
  {
    name: "Free Operations Audit",
    pitch: "Find the gaps. Get the roadmap.",
    description:
      "A focused diagnostic of your current recruiting, operations, or data workflows, with a clear AI-implementation plan and quick-win recommendations.",
    price: "Free",
    priceNote: "30-minute session · no obligation",
    features: [
      "Workflow & tools review",
      "Quick-win AI opportunities",
      "90-day implementation plan",
      "Live walkthrough & recommendations",
    ],
    cta: "Get My Free Audit",
    pillar: "audit",
    highlighted: false,
  },
  {
    name: "System Setup",
    pitch: "We design and install your operating layer.",
    description:
      "A done-for-you AI-enabled workflow built around your roles, tools, and team — implemented and running in about 30 days.",
    price: "Project",
    priceNote: "Flat-fee · scoped to your business",
    features: [
      "Custom AI-assisted workflow build",
      "Tool + ATS/CRM integration",
      "Team enablement & SOPs",
      "30 days of tuning & support",
    ],
    cta: "Talk to Us About Setup",
    pillar: "system-setup",
    highlighted: true,
  },
  {
    name: "Ops Partner",
    pitch: "We run it with you, week after week.",
    description:
      "Your fractional operations team — AI workflows plus trained specialists delivering ongoing work on retainer, with weekly reporting.",
    price: "Monthly",
    priceNote: "Retainer · 3-month minimum",
    features: [
      "Everything in System Setup",
      "Ongoing delivery by specialists",
      "AI-assisted daily execution",
      "Weekly reporting & reviews",
      "Dedicated ops lead",
    ],
    cta: "Explore the Partnership",
    pillar: "ops-partner",
    highlighted: false,
  },
];

export default function HomeEngagement() {
  return (
    <section
      id="engagement"
      data-testid="engagement-section"
      className="relative py-24 sm:py-32"
    >
      <div className="absolute inset-0 bg-radial-cyan opacity-40" />
      <div className="relative max-w-[1400px] mx-auto px-6 sm:px-10">
        <Chapter
          number="06"
          eyebrow="How To Work With Us"
          align="center"
          title={
            <>
              Start with an audit.{" "}
              <span className="text-gradient-cyan">Scale into operations.</span>
            </>
          }
          subtitle="Every engagement starts with a free audit. From there, you can move into a system build or a full operations partnership."
        />

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-6 items-stretch">
          {packages.map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: i * 0.08 }}
              data-testid={`package-card-${i}`}
              className={`relative rounded-2xl p-8 flex flex-col ${
                p.highlighted
                  ? "border border-[#00E5FF]/50 bg-gradient-to-b from-[#0B1A33] to-[#06101F] shadow-[0_0_60px_-20px_rgba(0,229,255,0.5)]"
                  : "border border-slate-800 bg-[#0A1325]/70"
              }`}
            >
              {p.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-[#00E5FF] text-[#050B14] text-[10px] font-bold tracking-[0.18em] uppercase px-3 py-1 rounded-full">
                  <Sparkles size={11} /> Most Popular
                </div>
              )}
              <div>
                <h3 className="font-display text-xl font-semibold text-white">
                  {p.name}
                </h3>
                <p className="mt-1.5 text-sm text-[#A3FF5E] font-medium">
                  {p.pitch}
                </p>
                <p className="mt-4 text-sm leading-relaxed text-slate-400">
                  {p.description}
                </p>
              </div>

              <div className="mt-6 pb-6 border-b border-slate-800">
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-3xl font-bold text-white">
                    {p.price}
                  </span>
                </div>
                <p className="mt-1 text-xs text-slate-500">{p.priceNote}</p>
              </div>

              <ul className="mt-6 space-y-3 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                    <Check
                      size={16}
                      className={`mt-0.5 shrink-0 ${
                        p.highlighted ? "text-[#A3FF5E]" : "text-[#00E5FF]"
                      }`}
                    />
                    {f}
                  </li>
                ))}
              </ul>

              <a
                href={calendlyUrl({ utm_content: p.pillar })}
                {...calendlyLinkProps}
                data-testid={`package-cta-${i}`}
                className={`group mt-8 inline-flex items-center justify-center gap-2 w-full font-semibold rounded-md h-12 transition-all ${
                  p.highlighted
                    ? "bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-[#050B14] shadow-[0_0_30px_-8px_rgba(0,229,255,0.6)]"
                    : "border border-slate-700 hover:border-[#00E5FF]/60 text-white bg-white/[0.02] hover:bg-[#00E5FF]/[0.05]"
                }`}
              >
                {p.cta} <ArrowRight size={16} className="arrow-shift" />
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
